/**
 * One month for every month screen (decisions-log V74, supersedes the design's per-screen `months`):
 * switching it on «План проектов» switches «План выплат», «Счета», «Поступления» and «Выплаты» too.
 * `?month=` in the URL wins and becomes the shared month; else the remembered one; else the
 * previous calendar month (the month being closed).
 */
import { addMonths, isPeriod, todayIso } from '@outsource/shared';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { readStored, writeStored } from './storage';

const STORAGE_KEY = 'oms2:month';

const isMonth = (value: unknown): value is string => typeof value === 'string' && isPeriod(value);

let shared: string | null = readStored(STORAGE_KEY, isMonth) ?? null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = (): string | null => shared;

/** The month the screens open on when nothing was chosen yet. */
export function defaultMonth(): string {
  return addMonths(todayIso().slice(0, 7), -1);
}

export function rememberMonth(month: string): void {
  if (shared === month) return;
  shared = month;
  writeStored(STORAGE_KEY, month);
  listeners.forEach((listener) => listener());
}

/** The shared month of the month screens. */
export function useRememberedMonth(): string {
  return useSyncExternalStore(subscribe, snapshot, snapshot) ?? defaultMonth();
}

export interface MonthSearch {
  readonly month?: string;
}

/** Route `validateSearch` for month screens. */
export function validateMonthSearch(search: Record<string, unknown>): MonthSearch {
  return typeof search.month === 'string' && isPeriod(search.month) ? { month: search.month } : {};
}

/** The screen's month and a setter that updates the URL and the shared month. */
export function useScreenMonth(): readonly [string, (month: string) => void] {
  const search = useSearch({ strict: false }) as MonthSearch;
  const remembered = useRememberedMonth();
  const month = search.month ?? remembered;
  const navigate = useNavigate();

  useEffect(() => {
    rememberMonth(month);
  }, [month]);

  const setMonth = useCallback(
    (next: string) => {
      rememberMonth(next);
      void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, month: next }) });
    },
    [navigate],
  );
  return [month, setMonth];
}
